/**
 * WinsWebhookSetup — admin UI for configuring the per-org Slack
 * channel where ≥ $5K verified savings auto-broadcast a celebration.
 * Drops onto the Engineer Savings Wall page.
 *
 * Brand: Slate-950 base, emerald accents, soft amber for warnings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Slack, Loader2, CheckCircle2, ExternalLink, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WinsWebhookSetup = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [adminGated, setAdminGated] = useState(false);

  const auth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/engineer-savings-wall/wins-webhook`, { headers: auth() });
      setStatus(data);
      setAdminGated(false);
    } catch (e) {
      if (e?.response?.status === 403) setAdminGated(true);
      else toast.error('Could not load Slack wins config');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  // Hide the panel completely from non-admins (don't render misleading state).
  if (adminGated) return null;

  const handleSave = async () => {
    const v = url.trim();
    if (!v.startsWith('https://hooks.slack.com/services/')) {
      toast.error('Webhook URL must start with https://hooks.slack.com/services/');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/engineer-savings-wall/wins-webhook`,
        { webhook_url: v }, { headers: auth() });
      toast.success('Wins channel configured. The next ≥$5K verified win will broadcast.');
      setUrl('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm('Stop broadcasting wins to Slack? You can reconfigure anytime.')) return;
    setRemoving(true);
    try {
      await axios.delete(`${API_URL}/engineer-savings-wall/wins-webhook`, { headers: auth() });
      toast.success('Wins broadcaster silenced.');
      await load();
    } catch {
      toast.error('Could not remove. Try again.');
    } finally { setRemoving(false); }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-white/10">
        <CardContent className="p-4 flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Slack wins config…
        </CardContent>
      </Card>
    );
  }

  if (status?.configured) {
    return (
      <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="wins-webhook-configured">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              <CardTitle className="text-base text-white">Slack wins broadcaster — live</CardTitle>
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                ≥ $5,000 / mo
              </Badge>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRemove}
              disabled={removing}
              className="border-rose-400/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="wins-webhook-remove-btn"
            >
              {removing
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Removing</>
                : <><Trash2 className="w-4 h-4 mr-1.5" /> Stop broadcasting</>}
            </Button>
          </div>
          <CardDescription className="text-slate-400 mt-2">
            Every verified savings action ≥{' '}
            <span className="text-emerald-300">$5,000/mo</span>{' '}
            posts a celebration to your wins channel — public proof of wins → engineering team morale loop → way more savings logged.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-white/10" data-testid="wins-webhook-cta">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Slack className="w-5 h-5 text-emerald-300" />
          <CardTitle className="text-base text-white">Broadcast wins to Slack</CardTitle>
          <Badge variant="outline" className="border-cyan-400/30 text-cyan-200 text-[11px]">
            Compounds referrals
          </Badge>
        </div>
        <CardDescription className="text-slate-400">
          Paste a Slack incoming-webhook URL for your wins channel. When an admin verifies a savings action ≥$5K/mo,
          CARTIE auto-posts a celebration message — engineers love social proof for shipped wins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/T0/B0/…"
            className="bg-slate-950 border-white/10 text-slate-100"
            data-testid="wins-webhook-input"
          />
          <Button
            onClick={handleSave}
            disabled={saving || url.trim().length < 20}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold whitespace-nowrap"
            data-testid="wins-webhook-save-btn"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving</>
              : <><Slack className="w-4 h-4 mr-1.5" /> Configure broadcaster</>}
          </Button>
        </div>
        <p className="text-xs text-slate-400 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3 text-amber-400" />
          We validate the URL must start with <code className="text-emerald-300">https://hooks.slack.com/services/</code>{' '}
          (anti-phishing).
        </p>
        <a
          href="https://api.slack.com/messaging/webhooks"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1.5"
        >
          How to get a Slack incoming-webhook URL <ExternalLink className="w-3 h-3" />
        </a>
      </CardContent>
    </Card>
  );
};

export default WinsWebhookSetup;
